<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import {
		parseURLState,
		updateURLState,
		getCurrentRoute,
		navigateTo,
		type URLState
	} from './url-state';
	import {
		getTrefwoorden,
		getLatestDateRange,
		createTrefwoord,
		updateTrefwoord,
		deleteTrefwoord as deleteTrefwoordApi
	} from './api';
	import type { IdMap } from './types';

	// Components
	import Summary from './components/Summary.svelte';
	import PerTrefwoord from './components/PerTrefwoord.svelte';
	import Search from './components/Search.svelte';
	import Import from './components/Import.svelte';
	import Modal from './components/Modal.svelte';
	import ToastContainer from './components/ToastContainer.svelte';
	
	// Error handling
	import { handleApiError, handleNetworkError } from './utils/errors';
	import { toast } from './stores/toast';

	let currentRoute = $state(getCurrentRoute());
	let urlState = $state<URLState>(parseURLState());
	let search = $state('');

	// Trefwoorden data
	let trefwoordIds = $state<number[]>([]);
	let id2trefwoord = $state<IdMap<string>>({});

	// Modal state
	let showModal = $state(false);
	let showModalDelete = $state(false);
	let currentTwId = $state<number>(0);
	let moveToId = $state(1);
	let newTrefw = $state(false);
	let newOmschrijving = $state('');

	// Handle URL changes
	function handleUrlChange() {
		currentRoute = getCurrentRoute();
		urlState = parseURLState();
		if (!urlState.from || !urlState.until) {
			initialize();
		}
	}

	// Handle date changes - simplified
	function handleDateChange() {
		updateURLState({ from: urlState.from, until: urlState.until });
		handleUrlChange();
	}

	// Handle search
	function handleSearch() {
		if (search) {
			navigateTo(`/search/${encodeURIComponent(search)}`, {
				from: urlState.from,
				until: urlState.until,
				search
			});
		}
	}

	// Initialize app
	async function initialize() {
		try {
			// Load trefwoorden
			const trefwoordenData = await getTrefwoorden();
			trefwoordIds = trefwoordenData.trefwoordIds;
			id2trefwoord = trefwoordenData.id2trefwoord;

			// Set default date range if not provided
			if (!urlState.from || !urlState.until) {
				const latest = await getLatestDateRange();
				urlState.from = urlState.from || latest.from;
				urlState.until = urlState.until || latest.until;
				updateURLState(urlState, true); // replace current URL
			}
		} catch (error) {
			console.error('Failed to initialize app:', error);
			if (error instanceof Error) {
				const errorResult = handleNetworkError(error);
				toast.apiError(errorResult);
			} else {
				toast.error('Kan applicatie niet laden. Probeer de pagina te vernieuwen.');
			}
		}
	}

	// Trefwoord management
	async function writeTrefwoord(id: number | null, omschrijving: string) {
		try {
			if (!omschrijving.trim()) {
				toast.warning('Trefwoord omschrijving kan niet leeg zijn');
				return;
			}

			if (id === null) {
				// Create new trefwoord
				await createTrefwoord(omschrijving);
				toast.success(`Trefwoord "${omschrijving}" toegevoegd`);
			} else {
				// Update existing trefwoord
				await updateTrefwoord(id, omschrijving);
				toast.success(`Trefwoord bijgewerkt naar "${omschrijving}"`);
			}

			// Refresh trefwoorden data
			const data = await getTrefwoorden();
			trefwoordIds = data.trefwoordIds;
			id2trefwoord = data.id2trefwoord;
		} catch (error) {
			console.error('Failed to write trefwoord:', error);
			if (error instanceof Error) {
				const errorResult = handleNetworkError(error);
				toast.apiError(errorResult);
			} else {
				toast.error('Kon trefwoord niet opslaan');
			}
		}
	}

	async function deleteTrefwoord(id: number, omschrijving: string, moveToId: number) {
		try {
			await deleteTrefwoordApi(id, moveToId);
			
			// Refresh trefwoorden data
			const data = await getTrefwoorden();
			trefwoordIds = data.trefwoordIds;
			id2trefwoord = data.id2trefwoord;

			// Close the delete modal
			showModalDelete = false;
			
			toast.success(`Trefwoord "${omschrijving}" verwijderd`);
		} catch (error) {
			console.error('Failed to delete trefwoord:', error);
			if (error instanceof Error) {
				const errorResult = handleNetworkError(error);
				toast.apiError(errorResult);
			} else {
				toast.error('Kon trefwoord niet verwijderen');
			}
		}
	}

	async function writeNewTrefwoord() {
		await writeTrefwoord(null, newOmschrijving);
		newTrefw = false;
		newOmschrijving = '';
	}

	onMount(() => {
		initialize();
		window.addEventListener('popstate', handleUrlChange);
	});

	onDestroy(() => {
		window.removeEventListener('popstate', handleUrlChange);
	});
</script>

<nav class="header">
	<label
		>van<input
			type="date"
			name="from"
			bind:value={urlState.from}
			onchange={handleDateChange}
		/></label
	>
	<label
		>tot<input
			type="date"
			name="until"
			bind:value={urlState.until}
			onchange={handleDateChange}
		/></label
	>
	<input
		type="text"
		bind:value={search}
		placeholder="Zoeken"
		title="zoek op rekeningnummer of omschrijving"
		onkeydown={(e) => e.key === 'Enter' && handleSearch()}
	/>
	<div>
		<button onclick={() => (showModal = true)}>Trefwoorden</button>
		<button onclick={() => navigateTo('/importeer', { from: urlState.from, until: urlState.until })}>Importeer</button>
	</div>
</nav>

<main>
	{#if currentRoute === 'summary'}
		<Summary {urlState} {id2trefwoord} />
	{:else if currentRoute === 'pertrefwoord'}
		<PerTrefwoord {urlState} {id2trefwoord} {trefwoordIds} />
	{:else if currentRoute === 'search'}
		<Search {urlState} {id2trefwoord} {trefwoordIds} />
	{:else if currentRoute === 'importeer'}
		<Import {urlState} />
	{/if}

	<Modal bind:showModal>
		<h2>Trefwoorden</h2>
		{#if trefwoordIds.length > 0}
			<button
				class="small"
				onclick={() => {
					newTrefw = !newTrefw;
				}}>+</button
			>
			<div class="alltrefw">
				{#if newTrefw}
					<div
						id="tw-input"
						contenteditable="true"
						bind:innerHTML={newOmschrijving}
						onblur={writeNewTrefwoord}
					>
						&nbsp;
					</div>
					<div>&nbsp;</div>
				{/if}
				{#each trefwoordIds as twId (twId)}
					<div
						contenteditable="true"
						bind:innerHTML={id2trefwoord[twId]}
						onblur={() => writeTrefwoord(twId, id2trefwoord[twId])}
					>
						&nbsp;
					</div>
					{#if twId !== 1}
						<button
							class="small"
							onclick={() => {
								showModalDelete = true;
								currentTwId = twId;
							}}><img class="but" src="/trashcan.svg" alt="delete" /></button
						>
					{:else}<div>&nbsp;</div>
					{/if}
				{/each}
			</div>
		{/if}
	</Modal>

	<Modal bind:showModal={showModalDelete}>
		<h2>Verwijderen trefwoord "{id2trefwoord[currentTwId]}"</h2>
		{#if trefwoordIds.length > 0 && currentTwId}
			<p>Gekoppelde overschrijvingen koppelen aan:</p>
			Trefwoord:<select bind:value={moveToId}>
				{#each trefwoordIds as twId (twId)}
					<option value={twId}>{id2trefwoord[twId]}</option>
				{/each}
			</select>
			<div class="right">
				<button onclick={() => deleteTrefwoord(currentTwId, id2trefwoord[currentTwId], moveToId)}
					>Verwijder</button
				>
			</div>
		{/if}
	</Modal>
</main>

<!-- Toast notifications container -->
<ToastContainer />

<style>
	nav {
		display: flex;
		flex-wrap: wrap;
		gap: 1em;
		padding: 0.5em 1em;
		margin-bottom: 2em;
	}
	input {
		margin-left: 0.5em;
		font: inherit;
	}
	nav div {
		margin-left: auto;
	}
	#tw-input {
		outline: 2px solid var(--clr-bg-200);
		border-radius: var(--br);
	}
	.alltrefw {
		display: grid;
		grid-template-columns: auto auto;
		gap: 0.5em;
	}
	.but {
		max-width: 1em;
		max-height: 1em;
		filter: invert();
	}
	h2 {
		margin-bottom: 1em;
	}
	button {
		cursor: pointer;
		padding: 0.5em 1em;
		border: 1px solid #ccc;
		border-radius: 4px;
		background: white;
		color: black;
		text-decoration: none;
		display: inline-block;
	}
	button:hover {
		background: #f0f0f0;
	}
	.small {
		font-size: 0.8em;
	}
	.right {
		text-align: right;
	}
</style>
